import { ROUTES } from '@/constants/routes'
import { useMsalLogin } from '@/hooks/Auth/useMsalLogin'
import { useAuthStore } from '@/store/auth'
import { useThemeStore } from '@/store/theme'
import useChatStore from '@/store/useChatStore'
import usePromptInputStore from '@/store/usePromptInputStore'
import {
  Avatar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import {
  IconAsteriskSimple,
  IconLogout,
  IconMoon,
  IconPlus,
  IconSmartHome,
  IconSun,
  IconTriangleSquareCircle,
  IconUserSearch,
} from '@tabler/icons-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const DRAWER_WIDTH = 65

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    overflowX: 'hidden',
    border: 'none',
    boxShadow: theme.shadows[1],
    background: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper,
    backdropFilter: 'blur(8px)',
  },
}))

const menuItems = [
  {
    text: 'Home',
    icon: <IconSmartHome size={24} stroke={1.5} />,
    path: ROUTES.DASHBOARD,
  },
  {
    text: 'Spaces',
    icon: <IconAsteriskSimple size={24} stroke={1.5} />,
    path: ROUTES.SPACES.CONVERSATIONS,
  },
  {
    text: 'Tools',
    icon: <IconTriangleSquareCircle size={24} stroke={1.5} />,
    path: ROUTES.TOOLS.ROOT,
  },
  {
    text: 'Network',
    icon: <IconUserSearch size={24} stroke={1.5} />,
    path: ROUTES.NETWORK.ROOT,
  },
]

const getInitials = (name: string | undefined): string => {
  if (!name) return 'U'
  const names = name.split(' ')
  return names.length > 1
    ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
    : names[0].substring(0, 2).toUpperCase()
}

export const MiniSidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useMsalLogin()
  const { user } = useAuthStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const theme = useTheme()
  const { toggleTheme } = useThemeStore()
  const { clearAll, setConversationId } = useChatStore()
  const { clearPrompt, setHasStartedConversation } = usePromptInputStore()

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleMenuClose()
    logout()
  }

  return (
    <StyledDrawer variant="permanent">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Tooltip title="New Conversation">
            <Box
              sx={{
                width: 40,
                height: 40,
                my: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                backgroundColor: 'primary.main',
                color: 'common.white',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  cursor: 'pointer',
                },
              }}
              onClick={() => {
                // Clear all related stores before navigating to new conversation
                clearAll()
                setConversationId(null)
                clearPrompt()
                setHasStartedConversation(false)

                // Dispatch custom event to clear hazards data
                // Using a try-catch to ensure the navigation happens even if dispatch fails
                try {
                  document.dispatchEvent(new CustomEvent('reset-hazards'))
                  console.log('Dispatched reset-hazards event from MiniSidebar')
                } catch (e) {
                  console.error('Failed to dispatch reset-hazards event:', e)
                }

                // Navigate to new conversation route
                navigate(ROUTES.CONVERSATION.DETAIL('new'))
              }}
            >
              <IconPlus size={24} stroke={1.5} />
            </Box>
          </Tooltip>
        </Box>

        <List sx={{ flex: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path)
            return (
              <ListItem
                key={item.text}
                disablePadding
                sx={{
                  flexDirection: 'column',
                  height: 'auto',
                  pb: 1.5,
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (!location.pathname.startsWith(item.path)) {
                      navigate(item.path)
                    }
                  }}
                  sx={{
                    flexDirection: 'column',
                    minHeight: 52,
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                      },
                      '& .MuiTypography-root': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                      },
                      '& .MuiTypography-root': {
                        color: 'primary.main',
                      },
                    },
                  }}
                  selected={isActive}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 'auto',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      mb: 0.75,
                      transition: 'color 0.2s ease-in-out',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontSize: '0.75rem',
                      textAlign: 'center',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {item.text}
                  </Typography>
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>

        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.overlay.dark.surfaceLight
                : theme.palette.overlay.light.surfaceLight,
          }}
        >
          <Avatar
            src={user?.photoUrl ?? undefined}
            onClick={handleMenuOpen}
            sx={{
              width: 44,
              height: 44,
              mb: 0.75,
              cursor: 'pointer',
              bgcolor: !user?.photoUrl ? 'primary.main' : undefined,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                opacity: 0.8,
                boxShadow: theme.shadows[2],
              },
            }}
          >
            {!user?.photoUrl && getInitials(user?.displayName)}
          </Avatar>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 120,
                  mt: 0.5,
                  boxShadow: theme.shadows[2],
                  '& .MuiList-root': {
                    py: 0.5,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={toggleTheme}
              sx={{
                py: 1,
                px: 1.5,
                fontSize: '0.875rem',
                gap: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {theme.palette.mode === 'dark' ? (
                <>
                  <IconSun size={18} stroke={1.5} />
                  Light Mode
                </>
              ) : (
                <>
                  <IconMoon size={18} stroke={1.5} />
                  Dark Mode
                </>
              )}
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1,
                px: 1.5,
                fontSize: '0.875rem',
                gap: 1.5,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: theme.palette.error.light,
                },
              }}
            >
              <IconLogout size={18} stroke={1.5} />
              Logout
            </MenuItem>
          </Menu>
          <Tooltip title={user?.displayName ?? user?.givenName}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                textAlign: 'center',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              {user?.givenName ?? user?.displayName?.split(' ')[0]}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </StyledDrawer>
  )
}
